import React from "react";
import dayjs from "dayjs";
import TrustpilotStars5 from "lib/assets/trustpilot-stars-5.svg";
import { TrustpilotReview } from "domain/reviews/types";
import { useLocale, useTranslations } from "lib/i18n";

const REVIEW_TEXT_MAX = 280;

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

const STAR_HEIGHT = 20;
const STARS_RATIO = 512 / 96;

const StarRow: React.FC = () => (
  <TrustpilotStars5
    aria-hidden="true"
    width={Math.round(STAR_HEIGHT * STARS_RATIO)}
    height={STAR_HEIGHT}
    css={`
      display: block;
    `}
  />
);

const ReviewCard: React.FC<{ review: TrustpilotReview }> = ({ review }) => {
  const t = useTranslations("common");
  const locale = useLocale();
  const truncated = truncate(review.text, REVIEW_TEXT_MAX);
  const dateLabel = dayjs(review.createdAt)
    .locale(locale)
    .format(locale === "es" ? "D [de] MMMM [de] YYYY" : "MMMM D, YYYY");
  const url = `https://www.trustpilot.com/reviews/${review.id}`;
  const reviewerLine = review.consumer.location
    ? `${review.consumer.displayName} · ${review.consumer.location}`
    : review.consumer.displayName;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("trustpilot.reviewAria", {
        name: review.consumer.displayName,
      })}
      css={`
        display: block;
        text-decoration: none;
        color: inherit;
      `}
    >
      <article
        css={`
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 24px;
          border-radius: 12px;
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          height: 100%;
          min-height: 180px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          :hover,
          a:focus-visible & {
            border-color: #00b67a;
            box-shadow: 0 4px 12px rgba(0, 182, 122, 0.15);
          }
        `}
      >
        <div
          aria-label={t("trustpilot.starsAria")}
          css={`
            line-height: 0;
          `}
        >
          <StarRow />
        </div>
        {review.title && (
          <h3
            css={`
              margin: 0;
              font-size: 16px;
              font-weight: 700;
              line-height: 1.4;
              color: #111827;
            `}
          >
            {review.title}
          </h3>
        )}
        <p
          css={`
            margin: 0;
            flex: 1;
            font-size: 14px;
            line-height: 1.55;
            color: #374151;
          `}
        >
          {truncated}
        </p>
        <footer
          css={`
            font-size: 12px;
            color: #6b7280;
          `}
        >
          {reviewerLine}
          <span aria-hidden="true"> · </span>
          <span>{dateLabel}</span>
        </footer>
      </article>
    </a>
  );
};

export default ReviewCard;
